/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// React
import React from 'react';
// redux hooks
import { useFieldArray, Controller } from 'react-hook-form';
// BASE components
import BASESvgIcon from '../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from '../../../../../../@BASE/utils/BASEUtils';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/system/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from '@mui/material/Typography';
// custom components
import NumberedHeading from '../../../../../shared-components/forms/NumberedHeading';
import LureFeedbackArray from '../shared/LureFeedbackArray';
import LureNoFeedbackArray from '../shared/LureNoFeedbackArray';
import LureAudioNoFeedbackArray from '../shared/LureAudioNoFeedbackArray';
import LureNoFeedbackEnglishArray from '../shared/LureNoFeedbackEnglishArray';
import QuestionMaterialsArray from '../shared/QuestionMaterialsArray';
// data
import { ReviewData } from '../../data/Defaults';

export default function MCQuestionArray({
  control,
  register,
  setValue,
  getValues,
  errors,
  inputDir,
  watch,
  materialPages
}) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.core'
  });

  const renderLureArray = (type_id, index) => {
    switch (type_id) {
      case 4:
        return (
          <Controller
            control={control}
            name={`content.core.${index}.lures`}
            render={({ field }) => (
              <LureAudioNoFeedbackArray
                nestIndex={index}
                inputDir={inputDir}
                {...{ control, errors }}
              />
            )}
          />
        );
      case 2:
        return (
          <Controller
            control={control}
            name={`content.core.${index}.lures`}
            render={({ field }) => (
              <LureFeedbackArray
                nestIndex={index}
                inputDir={inputDir}
                {...{ control, errors }}
              />
            )}
          />
        );
      case 3:
        return (
          <Controller
            control={control}
            name={`content.core.${index}.lures`}
            render={({ field }) => (
              <LureNoFeedbackEnglishArray
                nestIndex={index}
                inputDir={inputDir}
                {...{ control, errors }}
              />
            )}
          />
        );
      default:
        return (
          <Controller
            control={control}
            name={`content.core.${index}.lures`}
            render={({ field }) => (
              <LureNoFeedbackArray
                nestIndex={index}
                inputDir={inputDir}
                {...{ control, errors }}
              />
            )}
          />
        );
    }
  };

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0
          }
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{ marginTop: '22px' }} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="grow m-0 font-semibold">
                      Question {index + 1}
                    </Typography>
                    {index !== 0 && (
                      <IconButton
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:arrow-circle-up
                        </BASESvgIcon>
                      </IconButton>
                    )}
                    {index !== fields.length - 1 && (
                      <IconButton
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:arrow-circle-down
                        </BASESvgIcon>
                      </IconButton>
                    )}
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>
                        heroicons-outline:trash
                      </BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box className="p-0">
                      <NumberedHeading
                        number="1"
                        title="Materials"
                        instructions="Using the controls below, please add materials for the question."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.materials`}
                        render={({ field }) => (
                          <QuestionMaterialsArray
                            nestIndex={index}
                            inputDir={inputDir}
                            materialPages={materialPages}
                            {...{ control, errors }}
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="2"
                        title="Question Type"
                        instructions="Select the type of question."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.type_id`}
                        render={({ field }) => (
                          <Controller
                            control={control}
                            name={`content.core.${index}.type_id`}
                            render={({ field }) => (
                              <FormControl
                                fullWidth
                                className="mt-16"
                                error={!!errors.content?.core?.[index]?.type_id}
                              >
                                <Select
                                  {...field}
                                  id="template_id"
                                  classes={{
                                    select: 'flex items-center space-x-12'
                                  }}
                                  displayEmpty
                                  inputProps={{ 'aria-label': 'Question Type' }}
                                  required
                                >
                                  <MenuItem
                                    value=""
                                    disabled
                                    className="space-x-12"
                                  >
                                    Select a question type
                                  </MenuItem>
                                  <MenuItem value={0} className="space-x-12">
                                    Reading Comprehension (Text Prompt & Text
                                    Lures)
                                  </MenuItem>
                                  <MenuItem value={1} className="space-x-12">
                                    Listening Comprehension (Text Prompt with
                                    timestamps & Text Lures)
                                  </MenuItem>
                                  <MenuItem value={2} className="space-x-12">
                                    Statement Completion (Text Prompt & Text
                                    Lures)
                                  </MenuItem>
                                  <MenuItem value={3} className="space-x-12">
                                    Translation Analysis (Audio Prompt & Text
                                    Lures)
                                  </MenuItem>
                                  <MenuItem value={4} className="space-x-12">
                                    Skill Integration (Text Prompt & Audio
                                    Lures)
                                  </MenuItem>
                                  <MenuItem value={5} className="space-x-12">
                                    Question Selection (Text Prompt & Text
                                    Lures)
                                  </MenuItem>
                                </Select>
                                {!!errors.content?.core?.[index]?.type_id && (
                                  <FormHelperText>
                                    {
                                      errors.content?.core?.[index]?.type_id
                                        ?.message
                                    }
                                  </FormHelperText>
                                )}
                              </FormControl>
                            )}
                          />
                        )}
                      />

                      {watch(`content.core.${index}.type_id`) === '' ? (
                        <div className="w-100 mx-32 my-96">
                          <div className="flex items-center justify-center">
                            <BASESvgIcon size={24}>
                              heroicons-outline:exclamation
                            </BASESvgIcon>
                            <Typography className="text-16 p-16">
                              Please select a question type.
                            </Typography>
                          </div>
                        </div>
                      ) : (
                        <>
                          <NumberedHeading
                            number="3"
                            title="Prompt"
                            instructions="Enter a prompt in the target language."
                          />
                          <Controller
                            control={control}
                            name={`content.core.${index}.prompt`}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  className="mt-16 mb-16"
                                  label="Text"
                                  placeholder="Text"
                                  error={
                                    !!errors.content?.core?.[index]?.prompt
                                  }
                                  helperText={
                                    errors.content?.core?.[index]?.prompt
                                      ?.message
                                  }
                                  fullWidth
                                  multiline
                                  minRows={5}
                                  maxRows={10}
                                  required
                                  dir={inputDir}
                                />
                              </>
                            )}
                          />
                          {(watch(`content.core.${index}.type_id`) === 1 ||
                            watch(`content.core.${index}.type_id`) === 3) && (
                            <>
                              <Divider className="mt-16" />

                              <NumberedHeading
                                number="3"
                                title="Prompt Time Stamp"
                                instructions="Add the trimmed timestamp of question prompt."
                              />
                              <Grid container spacing={1}>
                                <Grid item xs={6} sm={3}>
                                  <Controller
                                    control={control}
                                    name={`content.core.${index}.start_minute`}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        className="mt-16 mb-16"
                                        label="Start Time (Minute)"
                                        placeholder="Start Time (Minute)"
                                        error={
                                          !!errors.content?.core?.[index]
                                            ?.start_minute
                                        }
                                        helperText={
                                          errors.content?.core?.[index]
                                            ?.start_minute?.message
                                        }
                                        fullWidth
                                        required
                                        type="number"
                                      />
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Controller
                                    control={control}
                                    name={`content.core.${index}.start_second`}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        className="mt-16 mb-16"
                                        label="Start Time (Second)"
                                        placeholder="Start Time (Second)"
                                        error={
                                          !!errors.content?.core?.[index]
                                            ?.start_second
                                        }
                                        helperText={
                                          errors.content?.core?.[index]
                                            ?.start_second?.message
                                        }
                                        fullWidth
                                        required
                                        type="number"
                                      />
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Controller
                                    control={control}
                                    name={`content.core.${index}.end_minute`}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        className="mt-16 mb-16"
                                        label="End Time (Minute)"
                                        placeholder="End Time (Minute)"
                                        error={
                                          !!errors.content?.core?.[index]
                                            ?.end_minute
                                        }
                                        helperText={
                                          errors.content?.core?.[index]
                                            ?.end_minute?.message
                                        }
                                        fullWidth
                                        required
                                        type="number"
                                      />
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Controller
                                    control={control}
                                    name={`content.core.${index}.end_second`}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        className="mt-16 mb-16"
                                        label="End Time (Second)"
                                        placeholder="End Time (Second)"
                                        error={
                                          !!errors.content?.core?.[index]
                                            ?.end_second
                                        }
                                        helperText={
                                          errors.content?.core?.[index]
                                            ?.end_second?.message
                                        }
                                        fullWidth
                                        required
                                        type="number"
                                      />
                                    )}
                                  />
                                </Grid>
                              </Grid>
                            </>
                          )}

                          <Divider className="mt-16" />

                          <NumberedHeading
                            number="4"
                            title="Lures"
                            instructions="Using the controls below, please add lures linked to this prompt."
                          />
                          {renderLureArray(
                            watch(`content.core.${index}.type_id`),
                            index
                          )}

                          <Divider className="mt-16" />

                          <NumberedHeading
                            number="5"
                            title="Correct Feedback"
                            instructions="Enter the feedback for a correct response"
                          />
                          <Controller
                            control={control}
                            name={`content.core.${index}.correct_feedback`}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  className="mt-16 mb-16"
                                  label="Text"
                                  placeholder="Text"
                                  error={
                                    !!errors.content?.core?.[index]
                                      ?.correct_feedback
                                  }
                                  helperText={
                                    errors.content?.core?.[index]
                                      ?.correct_feedback?.message
                                  }
                                  fullWidth
                                  multiline
                                  minRows={1}
                                  maxRows={5}
                                  required
                                  dir={inputDir}
                                />
                              </>
                            )}
                          />

                          <Divider className="mt-16" />

                          <NumberedHeading
                            number="6"
                            title="First Incorrect Feedback"
                            instructions="Enter the feedback for the first incorrect response."
                          />
                          <Controller
                            control={control}
                            name={`content.core.${index}.incorrect_feedback_1`}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  className="mt-16 mb-16"
                                  label="Text"
                                  placeholder="Text"
                                  error={
                                    !!errors.content?.core?.[index]
                                      ?.incorrect_feedback_1
                                  }
                                  helperText={
                                    errors.content?.core?.[index]
                                      ?.incorrect_feedback_1?.message
                                  }
                                  fullWidth
                                  multiline
                                  minRows={1}
                                  maxRows={5}
                                  required
                                  dir={inputDir}
                                />
                              </>
                            )}
                          />

                          <Divider className="mt-16" />

                          <NumberedHeading
                            number="7"
                            title="Second Incorrect Feedback"
                            instructions="Enter the feedback for the second incorrect response."
                          />
                          <Controller
                            control={control}
                            name={`content.core.${index}.incorrect_feedback_2`}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  className="mt-16 mb-16"
                                  label="Text"
                                  placeholder="Text"
                                  error={
                                    !!errors.content?.core?.[index]
                                      ?.incorrect_feedback_2
                                  }
                                  helperText={
                                    errors.content?.core?.[index]
                                      ?.incorrect_feedback_2?.message
                                  }
                                  fullWidth
                                  multiline
                                  minRows={1}
                                  maxRows={5}
                                  required
                                  dir={inputDir}
                                />
                              </>
                            )}
                          />

                          <Divider className="mt-16" />

                          <NumberedHeading
                            number="8"
                            title="Third Incorrect Feedback"
                            instructions="Enter the feedback for the third incorrect response."
                          />
                          <Controller
                            control={control}
                            name={`content.core.${index}.incorrect_feedback_3`}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  className="mt-16 mb-16"
                                  label="Text"
                                  placeholder="Text"
                                  error={
                                    !!errors.content?.core?.[index]
                                      ?.incorrect_feedback_3
                                  }
                                  helperText={
                                    errors.content?.core?.[index]
                                      ?.incorrect_feedback_3?.message
                                  }
                                  fullWidth
                                  multiline
                                  minRows={1}
                                  maxRows={5}
                                  required
                                  dir={inputDir}
                                />
                              </>
                            )}
                          />
                        </>
                      )}

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="9"
                        title="Max Number of Tries"
                        instructions="Enter the amount of tries the learner should get."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.max_number_of_tries`}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="mt-16 mb-16"
                            label="Max Number of Tries"
                            placeholder="Max Number of Tries"
                            error={
                              !!errors.content?.core?.[index]
                                ?.max_number_of_tries
                            }
                            helperText={
                              errors.content?.core?.[index]?.max_number_of_tries
                                ?.message
                            }
                            fullWidth
                            required
                            type="number"
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="10"
                        title="Multiple Select"
                        instructions="Check the box if there is more than one correct answer."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.multi_select`}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                onChange={(ev) => {
                                  field.onChange(ev.target.checked);
                                }}
                                checked={field.value}
                              />
                            }
                            label="Multiple Select"
                          />
                        )}
                      />
                      <Divider className="mt-16" />
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 && (
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-up
                          </BASESvgIcon>
                        }
                        size="small"
                      >
                        Move Up
                      </Button>
                    )}
                    {index !== fields.length - 1 && (
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-down
                          </BASESvgIcon>
                        }
                        size="small"
                      >
                        Move Down
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={
                        <BASESvgIcon size={20}>
                          heroicons-outline:trash
                        </BASESvgIcon>
                      }
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() =>
            append({
              id: BASEUtils.generateGUID(),
              review: ReviewData,
              materials: [
                {
                  material_title_en: '',
                  material_title_tl: '',
                  link: '',
                  media_material: false
                }
              ],
              type_id: '',
              prompt: '',
              start_minute: 0,
              start_second: 0,
              end_minute: 0,
              end_second: 0,
              lures: [
                {
                  lure_text: '',
                  is_correct: false
                }
              ],
              correct_feedback: '',
              incorrect_feedback_1: '',
              incorrect_feedback_2: '',
              incorrect_feedback_3: '',
              max_number_of_tries: 3,
              multi_select: false
            })
          }
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add question
          </span>
        </Button>
      </Box>
    </>
  );
}
